import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { hourlyClient } from "../../lib/hourly-orpc-client";

// Type from the oRPC client
type ScheduleData = Awaited<ReturnType<typeof hourlyClient.schedule.getSchedule>>;

/**
 * Compute the Monday at `offsetWeeks` from the current week.
 * Extracted from CoverageHeatmap.tsx to be shared.
 */
export function mondayOf(offsetWeeks = 0): Date {
  const now = new Date();
  const day = (now.getDay() + 6) % 7;
  const mon = new Date(now);
  mon.setDate(now.getDate() - day + offsetWeeks * 7);
  mon.setHours(0, 0, 0, 0);
  return mon;
}

/**
 * Fetches schedule data for multiple weeks in parallel.
 * Returns a Map from weekOffset to schedule data (or null on failure).
 * Query keys match useHourlySchedule's format for cache reuse.
 */
export function useForecastWeeks(
  locationId: string | null,
  weekCount: number,
  enabled: boolean,
): {
  schedulesByWeek: Map<number, ScheduleData | null>;
  isLoading: boolean;
  hasAnyError: boolean;
} {
  const weekOffsets = useMemo(
    () => Array.from({ length: weekCount }, (_, i) => i),
    [weekCount],
  );

  const queries = useQueries({
    queries: weekOffsets.map((offset) => {
      const weekStart = mondayOf(offset);
      const weekStartIso = weekStart.toISOString();
      return {
        queryKey: ["hourly", "schedule", locationId, weekStartIso] as const,
        // ^ matches hourlyQueryKeys.schedule(locationId, startDate) in useHourlyData.ts
        queryFn: async () => {
          if (!locationId) throw new Error("locationId required");
          return hourlyClient.schedule.getSchedule({
            locationId,
            startDate: weekStartIso,
            period: "WEEK",
          });
        },
        enabled: enabled && Boolean(locationId),
        staleTime: 5 * 60 * 1000,
      };
    }),
  });

  const schedulesByWeek = useMemo(() => {
    const map = new Map<number, ScheduleData | null>();
    for (let i = 0; i < weekOffsets.length; i++) {
      const q = queries[i];
      if (q.isError) {
        map.set(weekOffsets[i], null);
      } else if (q.data) {
        map.set(weekOffsets[i], q.data);
      }
      // If still loading, don't add to map — caller treats missing keys as "not yet loaded"
    }
    return map;
  }, [queries, weekOffsets]);

  return {
    schedulesByWeek,
    isLoading: queries.some((q) => q.isLoading),
    hasAnyError: queries.some((q) => q.isError),
  };
}
